import type { ReactNode, MouseEvent } from "react";

import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { useClickOutside } from "hooks";

import { Button } from "../Button";
import { Icon } from "../Icon/Icon";
import s from "./Dropdown.module.scss";

type DropdownOption = {
  title: ReactNode;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  isActive?: boolean;
  disabled?: boolean;
  testId?: string;
  id?: string;
};

type DropdownProps = {
  btnChildren?: ReactNode;
  btnClassName?: string;
  btnId?: string;
  options?: DropdownOption[];
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  openToTopForce?: boolean;
  disabled?: boolean;
  testId?: string;
};

export const Dropdown = Scrivito.connect(
  ({
    btnChildren,
    btnClassName,
    btnId,
    options,
    onClick,
    openToTopForce = false,
    disabled,
    testId,
  }: DropdownProps) => {
    const btnChildrenFinalValue = btnChildren ?? (
      <Icon iconName="ellipsis-vertical-regular" />
    );

    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

    const [isMenuOpened, setIsMenuOpened] = useState<boolean>(false);
    const [openToTop, setOpenToTop] = useState<boolean>(openToTopForce);

    const toggleDropdown = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        setIsMenuOpened((prev) => !prev);
        onClick?.(event);
      },
      [onClick],
    );

    const handleClickOutsideDropdown = useCallback(() => {
      setIsMenuOpened((prev) => (prev ? !prev : prev));
    }, []);

    useClickOutside(dropdownRef, handleClickOutsideDropdown);

    useEffect(() => {
      const handleDirectionToOpen = () => {
        if (openToTopForce) {
          return;
        }

        const buttonBounding = dropdownRef.current?.getBoundingClientRect();
        const menuBounding = dropdownMenuRef.current?.getBoundingClientRect();

        const buttonBottom = buttonBounding?.bottom ?? 0;
        const menuHeight = menuBounding?.height ?? 0;
        const { innerHeight } = window;

        setOpenToTop(buttonBottom + menuHeight > innerHeight);
      };

      handleDirectionToOpen();

      const handleScroll = () => {
        if (!isMenuOpened) {
          return;
        }

        handleDirectionToOpen();
      };

      window.addEventListener("scroll", handleScroll);

      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }, [openToTopForce, isMenuOpened]);

    return (
      <div className="position-relative" ref={dropdownRef}>
        <Button
          classNames={cx("h-100 px-2 py-0 border-0", btnClassName)}
          id={btnId}
          onClick={toggleDropdown}
          color=""
          disabled={disabled}
          data-testid={testId}
        >
          {btnChildrenFinalValue}
        </Button>

        {isMenuOpened && (
          <div
            className={cx(s.dropdownMenu, {
              [s.dropdownMenuToTop]: openToTop,
            })}
            ref={dropdownMenuRef}
          >
            {options?.map((button, index) => (
              <div
                key={index}
                className={cx("p-0_5", {
                  "border-1 border-bottom border-gray-300":
                    index < options.length - 1,
                })}
              >
                <Button
                  className={cx(s.dropdownMenuItem, {
                    "bg-blue-300 text-white": button.isActive,
                  })}
                  color="option"
                  data-testid={button.testId}
                  onClick={button.onClick}
                  disabled={button.disabled}
                >
                  {button.title}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  },
);
